import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Field, reduxForm } from 'redux-form';
import { FormattedMessage } from 'react-intl';

import Clearfix from 'Common/Clearfix/Clearfix.jsx';
import { FormJournalAutocomplete } from 'Common/ReduxForm';
import Button from 'Common/CustomButtons/Button';
import { CustomFormInput, FormInput } from 'Common/Form';
import { requiredFileValidation, maxLengthValidation, maxFileSizeValidation, scrollerHandler } from 'Utils';
import CoAuthorForm from './CoAuthorForm.jsx';
import CoAuthor from './CoAuthor.jsx';
import FileUpload from 'Common/CustomUpload/FileUpload';
import FormGroup, { ContactInfoForm, CorporateInfoForm, ProfessionalInfoForm, IdsInfoForm } from 'Components/FormGroup';
import { COVER_LETTER_EXAMPLE_ID, MAX_FILE_SIZE, FILE_ACCEPT_COVER_LETTER, FILE_ACCEPT_MANUSCRIPT } from 'Constants';

const maxLength255 = maxLengthValidation(255);
const maxLength2000 = maxLengthValidation(2000);

const styles = theme => ({
    field: {
        padding: theme.spacing.unit * 2,
        textAlign: 'center',
    },
});

@reduxForm({
    form: 'submissionData',
    enableReinitialize: true,
    onSubmitFail: scrollerHandler,
})
class SubmissionForm extends PureComponent {
    /**
     * @type {Object}
     */
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        pristine: PropTypes.bool,
        submitting: PropTypes.bool,
        classes: PropTypes.object.isRequired,
        downloadFile: PropTypes.func,
        loading: PropTypes.bool,
        coauthorsList: PropTypes.array,
        removeCoauthor: PropTypes.func,
        addCoauthor: PropTypes.func,
        numChildren: PropTypes.number,
    };

    /**
     * @type {Object}
     */
    static contextTypes = {
        intl: PropTypes.object.isRequired,
    };

    handleDownload(event, fileId, fileTitle) {
        event.preventDefault();
        const { downloadFile } = this.props;
        downloadFile(fileId, fileTitle);
    }

    render() {
        const { handleSubmit, pristine, submitting, loading, coauthorsList, removeCoauthor, addCoauthor } = this.props;
        const coauthors = [];

        coauthorsList.forEach(item => {
            return coauthors.push(<CoAuthorForm key={item} number={item} removeCoauthor={removeCoauthor} />);
        });

        return (
            <form onSubmit={handleSubmit} id="submission.data" className="form-wrapper">
                <FormGroup title="form_groups.journal_selection" required>
                    <Field
                        withLabel
                        name="journal_id"
                        rowClassName="mm-form-field"
                        component={FormJournalAutocomplete}
                        label="journal_id"
                        placeholder="journal_id"
                    />
                </FormGroup>

                <FormGroup title="form_groups.author_info">
                    <ContactInfoForm required exclude={['email']} />

                    <ProfessionalInfoForm required exclude={['interests']} />

                    <CorporateInfoForm required />

                    <IdsInfoForm exclude={['orcid_id', 'orcid_link']} />
                </FormGroup>

                <FormGroup title="form_groups.coauthors">
                    <CoAuthor addCoauthor={addCoauthor} coauthors={coauthors} />
                </FormGroup>

                <FormGroup title="form_groups.manuscript_info" required>
                    <Field
                        name="title"
                        rowClassName="mm-form-field"
                        component={CustomFormInput}
                        type="text"
                        label="manuscript_title"
                        placeholder="manuscript_title"
                        withLabel
                        validate={[maxLength255]}
                    />
                    <div className="field-wrapper-textarea">
                        <Field
                            name="extract"
                            rowClassName="mm-form-field"
                            component={FormInput}
                            type="textarea"
                            label="extract"
                            placeholder="extract"
                            withLabel
                            validate={[maxLength2000]}
                        />
                    </div>
                    <div className="field-wrapper-textarea field-wrapper-textarea-small">
                        <Field
                            name="key_words"
                            rowClassName="mm-form-field"
                            component={FormInput}
                            type="textarea"
                            label="key_words"
                            placeholder="key_words"
                            withLabel
                            required={false}
                            validate={[maxLength2000]}
                        />
                    </div>
                    <div className="sc-files-row">
                        <div className="field-wrapper-file">
                            <Field
                                name="manuscript"
                                rowClassName="mm-form-field"
                                component={FileUpload}
                                type="file"
                                label="manuscript"
                                placeholder="manuscript"
                                withLabel
                                validate={[requiredFileValidation, maxFileSizeValidation]}
                                accept={FILE_ACCEPT_MANUSCRIPT}
                            />
                            <div className="field-wrapper-file-notification">
                                {this.context.intl.formatMessage({ id: 'files.max_size' }, { max: MAX_FILE_SIZE })}
                            </div>
                        </div>
                        <div className="field-wrapper-file">
                            <Field
                                name="cover_letter"
                                component={FileUpload}
                                type="file"
                                label="cover_letter"
                                placeholder="cover_letter"
                                withLabel
                                validate={[requiredFileValidation, maxFileSizeValidation]}
                                accept={FILE_ACCEPT_COVER_LETTER}
                                sample={<div className='download-sample-block-wrapper'>
                                    <span className='download-sample-text'>
                                        {this.context.intl.formatMessage({ id: 'button.submission.download_cover_letter' })}
                                    </span>
                                    <a className='download-sample-link' href="javascript:void(0)"
                                        onClick={ (e) => this.handleDownload(e, COVER_LETTER_EXAMPLE_ID, this.context.intl.formatMessage({ id: 'files.cover_letter_title' })) }
                                    >
                                        <FormattedMessage id={'button.submission.download_cover_letter_link'} />
                                    </a>
                                </div>}
                            />
                            <div className="field-wrapper-file-notification">
                                {this.context.intl.formatMessage({ id: 'files.max_size' }, { max: MAX_FILE_SIZE })}
                            </div>
                        </div>
                    </div>
                    <div className="field-wrapper-textarea">
                        <Field
                            name="comment"
                            component={FormInput}
                            type="textarea"
                            label="comment"
                            placeholder="comment"
                            withLabel
                            required={false}
                            validate={[maxLength255]}
                        />
                    </div>
                </FormGroup>

                <Button color="success" right type="submit" disabled={pristine || submitting || loading}>
                    <FormattedMessage id={'button.submission.create'} />
                </Button>
                <Clearfix />
            </form>
        );
    }
}

export default withStyles(styles)(SubmissionForm);



// WEBPACK FOOTER //
// ./src/layouts/Submission/SubmissionForm.jsx